import { useMemo, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { IoAdd, IoTrashOutline } from 'react-icons/io5';
import { Button, Input, Modal, QuietIconButton, Select, Textarea, Tooltip } from '@shared/ui';
import {
    useCreateProductRejectionTemplate,
    useDeleteProductRejectionTemplate,
    useProductRejectionTemplates,
    useSetProductApproval,
} from '../../hooks/useProducts';
import './RejectProductModal.css';

const CUSTOM_VALUE = 'custom';
const REASON_MAX_LENGTH = 2000;
const TITLE_MAX_LENGTH = 120;

export interface RejectProductModalProps {
    product: { id: number; name: string } | null;
    onClose: () => void;
}

export default function RejectProductModal({ product, onClose }: RejectProductModalProps) {
    const { data: templates = [], isLoading } = useProductRejectionTemplates();
    const createTemplate = useCreateProductRejectionTemplate();
    const deleteTemplate = useDeleteProductRejectionTemplate();
    const setApproval = useSetProductApproval();

    const [reason, setReason] = useState('');
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [isAdding, setIsAdding] = useState(false);
    const [newTitle, setNewTitle] = useState('');
    const [newText, setNewText] = useState('');

    const selectedTemplate = templates.find((t) => t.id === selectedId) ?? null;

    const options = useMemo(
        () => [
            { value: CUSTOM_VALUE, label: 'Своя причина' },
            ...templates.map((t) => ({ value: String(t.id), label: t.title })),
        ],
        [templates],
    );

    const handleClose = () => {
        setReason('');
        setSelectedId(null);
        setIsAdding(false);
        onClose();
    };

    const handleSelect = (next: string | number | null) => {
        if (next === null || next === CUSTOM_VALUE) {
            if (selectedTemplate && reason === selectedTemplate.text) setReason('');
            setSelectedId(null);
            return;
        }
        const template = templates.find((t) => t.id === Number(next));
        if (!template) return;
        setSelectedId(template.id);
        setReason(template.text);
    };

    const openAddForm = () => {
        setNewTitle('');
        setNewText(reason);
        setIsAdding(true);
    };

    const handleCreate = () => {
        const title = newTitle.trim();
        const text = newText.trim();
        if (!title || !text) return;
        createTemplate.mutate(
            { title, text },
            {
                onSuccess: (created) => {
                    setIsAdding(false);
                    setSelectedId(created.id);
                    setReason(created.text);
                },
                onError: (err) => {
                    notifications.show({
                        title: 'Ошибка',
                        message: err instanceof Error ? err.message : 'Не удалось сохранить шаблон',
                        color: 'red',
                    });
                },
            },
        );
    };

    const handleDelete = () => {
        if (!selectedTemplate) return;
        deleteTemplate.mutate(selectedTemplate.id, {
            onSuccess: () => setSelectedId(null),
            onError: (err) => {
                notifications.show({
                    title: 'Ошибка',
                    message: err instanceof Error ? err.message : 'Не удалось удалить шаблон',
                    color: 'red',
                });
            },
        });
    };

    const handleReject = () => {
        const trimmed = reason.trim();
        if (!product || !trimmed) return;
        setApproval.mutate(
            { id: product.id, approved: false, reason: trimmed },
            {
                onSuccess: () => {
                    handleClose();
                    notifications.show({
                        title: 'Товар отклонён',
                        message: 'Продавец увидит причину в кабинете.',
                        color: 'green',
                    });
                },
                onError: (err) => {
                    notifications.show({
                        title: 'Ошибка',
                        message: err instanceof Error ? err.message : 'Не удалось отклонить',
                        color: 'red',
                    });
                },
            },
        );
    };

    const title = isAdding
        ? 'Новый шаблон причины'
        : product
          ? `Отклонить товар «${product.name}»?`
          : 'Отклонить товар';

    return (
        <Modal isOpen={product !== null} onClose={handleClose} title={title} maxWidth={520}>
            {isAdding ? (
                <div className="reject-product-modal">
                    <Input
                        label="Тема шаблона"
                        placeholder="Например: Плохое фото"
                        value={newTitle}
                        onChange={(e) => setNewTitle(e.currentTarget.value)}
                        maxLength={TITLE_MAX_LENGTH}
                    />
                    <Textarea
                        label="Текст шаблона"
                        placeholder="Текст, который увидит продавец"
                        value={newText}
                        onChange={(e) => setNewText(e.currentTarget.value)}
                        minRows={4}
                        autosize
                        maxLength={REASON_MAX_LENGTH}
                    />
                    <div className="reject-product-modal__actions">
                        <Button variant="secondary" onClick={() => setIsAdding(false)}>
                            Назад
                        </Button>
                        <Button
                            variant="primary"
                            disabled={!newTitle.trim() || !newText.trim() || createTemplate.isPending}
                            loading={createTemplate.isPending}
                            onClick={handleCreate}
                        >
                            Сохранить шаблон
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="reject-product-modal">
                    <div className="reject-product-modal__template-row">
                        <Select
                            label="Шаблон причины"
                            data={options}
                            value={selectedId ?? CUSTOM_VALUE}
                            onChange={handleSelect}
                            allowDeselect={false}
                            disabled={isLoading}
                        />
                        {selectedTemplate && (
                            <Tooltip label="Удалить шаблон">
                                <QuietIconButton
                                    icon={<IoTrashOutline size={18} />}
                                    tone="danger"
                                    aria-label="Удалить шаблон"
                                    loading={deleteTemplate.isPending}
                                    onClick={handleDelete}
                                />
                            </Tooltip>
                        )}
                        <Tooltip label="Добавить шаблон">
                            <QuietIconButton
                                icon={<IoAdd size={20} />}
                                tone="accent"
                                aria-label="Добавить шаблон"
                                onClick={openAddForm}
                            />
                        </Tooltip>
                    </div>
                    <Textarea
                        label="Причина отклонения"
                        placeholder="Опишите, что нужно исправить"
                        value={reason}
                        onChange={(e) => setReason(e.currentTarget.value)}
                        minRows={4}
                        autosize
                        maxLength={REASON_MAX_LENGTH}
                    />
                    <div className="reject-product-modal__actions">
                        <Button variant="secondary" onClick={handleClose}>
                            Отмена
                        </Button>
                        <Button
                            variant="primary"
                            disabled={!reason.trim() || setApproval.isPending}
                            loading={setApproval.isPending}
                            onClick={handleReject}
                        >
                            Отклонить
                        </Button>
                    </div>
                </div>
            )}
        </Modal>
    );
}
